import { useEffect, useRef, useState } from 'react'
import { CheckCircle2, Clock, XCircle } from 'lucide-react'
import { Button } from '@airclou/ui'
import { waitForActivation } from '../../../services/billing'

/**
 * What the customer sees when Stripe sends them back.
 *
 * The honest bit: returning from Checkout does not mean the suite is paid for.
 * Stripe's webhook is what records the subscription, and it can arrive before
 * or after the redirect. So this waits for the suite to really be on its
 * subscription rather than congratulating someone whose payment has not landed
 * yet — and if it takes too long, it says so plainly instead of spinning
 * forever.
 */

export type CheckoutOutcome = 'complete' | 'cancelled'

export interface CheckoutReturnProps {
  outcome: CheckoutOutcome
  /** The suite that was bought — Checkout's return URL carries it. */
  suite?: string
  /** Called once the account is confirmed active, so the page can refresh. */
  onActivated: () => void
  onDismiss: () => void
}

type Phase = 'confirming' | 'active' | 'slow'

export function CheckoutReturn({ outcome, suite, onActivated, onDismiss }: CheckoutReturnProps) {
  const [phase, setPhase] = useState<Phase>('confirming')

  // Held in a ref so the poll below does not depend on it. Callers pass a
  // plain function, which is a new value on every parent render — as a
  // dependency it would abort and restart the poll each time the page
  // re-rendered, resetting the timeout and never reaching the "still
  // processing" message.
  const onActivatedRef = useRef(onActivated)
  useEffect(() => {
    onActivatedRef.current = onActivated
  }, [onActivated])

  useEffect(() => {
    if (outcome !== 'complete') return
    const controller = new AbortController()
    let cancelled = false

    void waitForActivation({ suite, signal: controller.signal }).then((result) => {
      if (cancelled) return
      if (result === 'active') {
        setPhase('active')
        onActivatedRef.current()
      } else {
        setPhase('slow')
      }
    })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [outcome, suite])

  if (outcome === 'cancelled') {
    return (
      <Banner
        icon={<XCircle className="h-5 w-5 text-muted-foreground" />}
        title="Checkout cancelled"
        body="Nothing was charged. Pick a plan whenever you're ready."
        onDismiss={onDismiss}
      />
    )
  }

  if (phase === 'confirming') {
    return (
      <Banner
        icon={<Clock className="h-5 w-5 animate-pulse text-primary" />}
        title="Confirming your payment"
        body="This usually takes a few seconds. You can stay on this page."
      />
    )
  }

  if (phase === 'active') {
    return (
      <Banner
        icon={<CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />}
        title="You're subscribed"
        body="Your plan is active. Thanks!"
        onDismiss={onDismiss}
      />
    )
  }

  return (
    <Banner
      icon={<Clock className="h-5 w-5 text-muted-foreground" />}
      title="Still processing"
      // Deliberately not an error. The payment is not lost — the webhook lands
      // when it lands, and saying "something went wrong" would be untrue and
      // would invite a second payment.
      body="Your payment went through and we're waiting on confirmation from Stripe. Refresh in a minute; if it still hasn't updated, contact support."
      onDismiss={onDismiss}
    />
  )
}

function Banner({
  icon,
  title,
  body,
  onDismiss,
}: {
  icon: React.ReactNode
  title: string
  body: string
  onDismiss?: () => void
}) {
  return (
    <div className="mb-6 flex items-start gap-3 rounded-lg border border-border bg-primary/5 p-4">
      <div className="mt-0.5">{icon}</div>
      <div className="flex-1">
        <p className="font-semibold text-foreground">{title}</p>
        <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      </div>
      {onDismiss && (
        <Button variant="outline" size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
      )}
    </div>
  )
}
